/* eslint-disable react-refresh/only-export-components -- intentional: this module exports
   the provider alongside its context hooks (useActiveMatchContext, useActiveBackgammonMatch,
   useActiveQcpMatch) so downstream screens (T4-T7) import them all from one path, per the
   feature plan (docs/superpowers/plans/2026-07-21-active-match-pill.md). */
import { createContext, useContext, useEffect, useMemo, useRef, type ReactNode } from 'react';

import {
  useBackgammonSocket,
  type BackgammonSocket,
} from '@qlashgeminiz/qgammon-board-ui';

import { env } from '@/env';
import { qgammonSocketAuth } from '@/lib/qgammon/auth';
import { QGAMMON_SOCKET_OPTIONS } from '@/lib/qgammon/socketOptions';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { clearActiveMatch } from '@/store/activeMatch.slice';
import { useQcpMatch, type QcpMatchSocket } from '@/hooks/useQcpMatch';
import {
  projectQgammon,
  projectQcp,
  type ActiveMatchProjection,
} from '@/lib/activeMatch/projection';
import type { ActiveMatchDescriptor } from '@/store/activeMatch.slice';

interface ActiveMatchContextValue {
  descriptor: ActiveMatchDescriptor | null;
  qgammon: BackgammonSocket;
  qcp: QcpMatchSocket;
  projection: ActiveMatchProjection | null;
  /** Resign the currently-active match (used when replacing it with a new one). */
  resignActive: () => void;
}

const ActiveMatchContext = createContext<ActiveMatchContextValue | null>(null);

/**
 * Owns the live match socket ABOVE the route tree so it survives navigation
 * (the server forfeits on disconnect, not on turn — see the spec). Both game
 * hooks are mounted unconditionally; only the active game's matchId is passed,
 * the other stays null (no socket). Clears the slice centrally when the match
 * ends so the socket tears down cleanly.
 */
export function ActiveMatchProvider({ children }: { children: ReactNode }) {
  const dispatch = useAppDispatch();
  const descriptor = useAppSelector((s) => s.activeMatch.current);

  // The shared hook is surface-agnostic: the TMA supplies the WS origin and the
  // handshake auth. `getAuth` (not `getAccessToken`) because the TMA's
  // resolution order is richer than "a Logto token" — per-user Logto token
  // first, else the dev-bypass fallback, else nothing (see lib/qgammon/auth.ts).
  const qgammon = useBackgammonSocket({
    wsUrl: env.qgammonWsUrl,
    matchId: descriptor?.game === 'qgammon' ? descriptor.matchId : null,
    getAuth: qgammonSocketAuth,
    // The gameserver rate-limits the handshake and rejects the excess with
    // `too_many_connections`; Socket.IO answers a rejected handshake by
    // reconnecting, so the stock 1–5s ceiling would keep a WebView knocking
    // right at the per-user budget. Same options as our own listener sockets.
    socketOptions: QGAMMON_SOCKET_OPTIONS,
  });
  const qcp = useQcpMatch(descriptor?.game === 'qcp' ? descriptor.matchId : null, {
    vsBot: descriptor?.vsBot,
  });

  // Clear the slice only when the ACTIVE match reaches its terminal signal —
  // and only if we've seen THAT match id live first. A game hook mounted across
  // matches can briefly still expose the previous match's terminal payload on
  // the first render of the next match; the liveRef guard ignores that stale
  // signal instead of wiping the fresh match.
  const liveRef = useRef<string | null>(null);
  useEffect(() => {
    if (!descriptor) return;
    const terminal =
      descriptor.game === 'qgammon' ? !!qgammon.ended : !!qcp.matchResult;
    if (!terminal) {
      liveRef.current = descriptor.matchId; // this match is live now
      return;
    }
    if (liveRef.current === descriptor.matchId) {
      dispatch(clearActiveMatch());
    }
  }, [descriptor, qgammon.ended, qcp.matchResult, dispatch]);

  const projection: ActiveMatchProjection | null = useMemo(() => {
    if (!descriptor) return null;
    return descriptor.game === 'qgammon'
      ? projectQgammon(qgammon, descriptor)
      : projectQcp(qcp);
  }, [descriptor, qgammon, qcp]);

  const value = useMemo<ActiveMatchContextValue>(
    () => ({
      descriptor,
      qgammon,
      qcp,
      projection,
      resignActive: () => {
        if (descriptor?.game === 'qgammon') qgammon.resign(1, 'match');
        else if (descriptor?.game === 'qcp') qcp.resign();
      },
    }),
    [descriptor, qgammon, qcp, projection],
  );

  return <ActiveMatchContext.Provider value={value}>{children}</ActiveMatchContext.Provider>;
}

export function useActiveMatchContext(): ActiveMatchContextValue {
  const ctx = useContext(ActiveMatchContext);
  if (!ctx) throw new Error('useActiveMatchContext must be used within ActiveMatchProvider');
  return ctx;
}

/** Backgammon match socket for the active match (screen consumer). */
export function useActiveBackgammonMatch(): BackgammonSocket {
  return useActiveMatchContext().qgammon;
}

/** QCP match socket for the active match (screen consumer). */
export function useActiveQcpMatch(): QcpMatchSocket {
  return useActiveMatchContext().qcp;
}
